/**
 * String-case helpers for turning machine values (enum/state slugs like
 * `under_review`) into readable UI labels. Centralised so every surface formats
 * the same way and we stop re-rolling `slug.replace(/_/g, ' ')` inline.
 *
 * Design note — sentence case, NOT Title Case. Slugs in this domain carry
 * acronyms (`pending_pep_aml`, `kyc_review`, `sla_breach`); blindly capitalising
 * every word yields ugly `Pending Pep Aml`. `humanize` therefore capitalises
 * only the first letter. Where a precise label matters (acronyms, branded
 * terms), prefer an explicit label map and fall back to `humanize` only for the
 * long tail — e.g. `fieldDef?.label ?? humanize(key)`.
 */

/** Capitalise the first character; leave the rest untouched. `apple` → `Apple`. */
export function capitalize(value: string | null | undefined): string {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Machine slug → readable label. Splits on `_` / `-` (and any whitespace),
 * collapses runs, trims, then sentence-cases the result.
 *   `under_review`     → `Under review`
 *   `pending-pep_aml`  → `Pending pep aml`
 *   `  no__answer  `   → `No answer`
 * Nullish / empty input returns `''`.
 */
export function humanize(value: string | null | undefined): string {
  if (!value) return '';
  const spaced = value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return capitalize(spaced);
}
